import { useReveal } from "@/hooks/useReveal";

const AV_HERO = "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/7bpzcfrn_9A477286-2930-40BC-A0B0-815E05268C8E.png";
const AV_2 = "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/xxvkq5an_2BCC1627-4054-4FA9-B6B6-96B9DF560544.png";
const AV_3 = "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/vehdzfru_BA376CB4-99C2-4D3B-8E04-D390C34C1D23.png";
const AV_4 = "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/8fq4tf57_390F8100-5E7A-4BA5-A9B5-20819DF67749.png";
const AV_5 = "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/8i5dnjtv_C4C4691B-C6A8-4448-BFBA-962B917AFDFE.png";

const SPECS = [
  { label: "Aircraft", value: "Gulfstream G650" },
  { label: "Range", value: "7,000 Nautical Miles" },
  { label: "Cabin", value: "Bespoke PiérrecDavís Interior" },
  { label: "Capacity", value: "Up to 14 Guests" },
  { label: "Service", value: "Dedicated Concierge Crew" },
  { label: "Discretion", value: "Absolute — Private Terminals" },
];

export default function AviationSection({ onRequest }) {
  const eyebrow = useReveal();
  const title = useReveal();
  const lead = useReveal();
  const heroR = useReveal();
  const g1 = useReveal();
  const g2 = useReveal();
  const g3 = useReveal();
  const g4 = useReveal();
  const detailR = useReveal();

  return (
    <section
      id="aviation"
      data-testid="aviation-section"
      className="relative bg-black py-28 md:py-40 overflow-hidden grain"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 -left-40 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(212,175,140,0.10)_0%,transparent_70%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p ref={eyebrow} className="pd-reveal eyebrow" data-testid="aviation-eyebrow">
            PiérrecDavís Private Aviation
          </p>
          <h2
            ref={title}
            className="pd-reveal mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.06em] leading-[1.05]"
            style={{ transitionDelay: "160ms" }}
            data-testid="aviation-title"
          >
            The Sky, <br />
            <span className="italic font-serif-body text-gold-gradient">Held in Private.</span>
          </h2>
          <div ref={lead} className="pd-reveal mt-8" style={{ transitionDelay: "320ms" }}>
            <p className="font-serif-body italic text-lg md:text-xl text-white/80 leading-relaxed">
              A journey should not be witnessed. It should be composed.
            </p>
            <p className="mt-4 text-white/55 text-sm leading-relaxed max-w-2xl mx-auto">
              For a small circle of clients, the maison arranges private aviation aboard a bespoke Gulfstream G650 — appointed to the standards that define PiérrecDavís.
            </p>
          </div>
        </div>

        {/* Hero image */}
        <div ref={heroR} className="pd-reveal relative aspect-[16/9] overflow-hidden border border-[var(--pd-border)] mb-6">
          <img
            src={AV_HERO}
            alt="PiérrecDavís Private Aviation — Gulfstream G650"
            className="w-full h-full object-cover transition-transform duration-[2400ms] hover:scale-105"
            data-testid="aviation-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 pointer-events-none" />
          <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
            <div>
              <p className="eyebrow text-white/70">Aircraft</p>
              <p className="font-serif-display text-2xl text-white mt-1 tracking-[0.18em]">Gulfstream G650</p>
            </div>
            <span className="text-[0.65rem] uppercase tracking-[0.4em] text-[var(--pd-gold)]">Maison Aviation</span>
          </div>
        </div>

        {/* Gallery grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          <div ref={g1} className="pd-reveal aspect-[4/5] overflow-hidden border border-[var(--pd-border)]">
            <img src={AV_2} alt="Cabin ambience" className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105" data-testid="aviation-gallery-1" />
          </div>
          <div ref={g2} className="pd-reveal aspect-[4/5] overflow-hidden border border-[var(--pd-border)]" style={{ transitionDelay: "120ms" }}>
            <img src={AV_3} alt="Interior appointments" className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105" data-testid="aviation-gallery-2" />
          </div>
          <div ref={g3} className="pd-reveal aspect-[4/5] overflow-hidden border border-[var(--pd-border)]" style={{ transitionDelay: "240ms" }}>
            <img src={AV_4} alt="Private service" className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105" data-testid="aviation-gallery-3" />
          </div>
          <div ref={g4} className="pd-reveal aspect-[4/5] overflow-hidden border border-[var(--pd-border)]" style={{ transitionDelay: "360ms" }}>
            <img src={AV_5} alt="Aviation detail" className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105" data-testid="aviation-gallery-4" />
          </div>
        </div>

        {/* Specs + CTA */}
        <div ref={detailR} className="pd-reveal grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <p className="eyebrow text-[var(--pd-gold)]">PiérrecDavís® · Aviation Program</p>
            <h3 className="font-serif-display text-3xl md:text-5xl text-white tracking-[0.06em] mt-4" data-testid="aviation-program-name">
              Private Charter <br /><span className="italic font-serif-body">Composed to Order.</span>
            </h3>
            <p className="mt-6 text-white/70 leading-relaxed font-light">
              Each flight is composed as a private commission — routing, provisioning, and cabin arranged in confidence around a single client. Departures from private terminals worldwide, without record and without spectacle.
            </p>
            <p className="mt-4 text-white/55 italic font-serif-body text-lg">
              The maison does not sell flights. It composes journeys.
            </p>
          </div>

          <div className="lg:col-span-6">
            <div className="space-y-3">
              {SPECS.map((s) => (
                <div
                  key={s.label}
                  data-testid={`aviation-spec-${s.label.toLowerCase()}`}
                  className="flex items-baseline justify-between gap-6 border-b border-white/10 pb-3"
                >
                  <span className="text-[0.65rem] uppercase tracking-[0.32em] text-white/50">{s.label}</span>
                  <span className="font-serif-body text-base text-white text-right">{s.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-baseline justify-between border-t border-[var(--pd-border)] pt-6">
              <span className="eyebrow">From</span>
              <span
                className="font-serif-display text-2xl md:text-3xl text-[var(--pd-gold)] tracking-[0.1em]"
                data-testid="aviation-price"
              >
                $85,000 USD
              </span>
            </div>
            <p className="mt-3 text-white/45 text-xs uppercase tracking-[0.32em] text-right">
              Per journey · Bespoke pricing arranged privately
            </p>

            <button
              data-testid="aviation-request-btn"
              onClick={onRequest}
              className="btn-gold mt-8 w-full md:w-auto"
            >
              <span>Request Private Appointment</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
